// ============================================================================
// src/lib/seo/deal-meta.ts
// PV-1052 · SEO lot 1 — dynamic head builders for /deals/:slug, from the real
// DealView row only. Every optional field degrades by OMISSION (no building →
// community, no image → default OG, no price / no positive discount → the
// sentence is dropped) — never by inventing data.
//
// The discount sentence follows discountDisplayMode (ADR-055) and the badge
// wording exactly: off-plan reads "below original price" (the reference is
// the developer's original price, not DLD), every DLD-backed perimeter reads
// the commercial "below market", suppressed shows nothing.
// ============================================================================

import { APP } from '@/config/app';
import type { DealView } from '@/lib/db/deals';
import { discountDisplayMode } from '@/lib/discount-display';
import { formatAed, formatDiscountMagnitude } from '@/lib/format';
import { DEFAULT_OG_IMAGE, SEO_ORIGIN, type SeoImage, type SeoMeta } from './head';

/**
 * The subset of DealView the SEO builders actually read. Kept as a Pick so a
 * full DealView satisfies it structurally (DealPage) while the build-time
 * prerender script (scripts/prerender-heads.mts) can feed a minimal mapping
 * of the raw row without stubbing unrelated fields.
 */
export type DealSeoInput = Pick<
  DealView,
  | 'slug'
  | 'community'
  | 'buildingTitle'
  | 'building'
  | 'bedrooms'
  | 'askingAed'
  | 'discountPct'
  | 'dldConfidence'
  | 'dldScope'
  | 'status'
  | 'dealType'
  | 'propertyType'
  | 'imageLabel'
  | 'imageUrl'
>;

// Fixed EN labels (same rationale as site.ts) — mirrors PROPERTY_TYPES
// (lib/db/deals.ts) rather than the i18n catalogue.filters options.
const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: 'Apartment',
  villa: 'Villa',
  townhouse: 'Townhouse',
  penthouse: 'Penthouse',
  plot: 'Plot',
  commercial: 'Commercial property',
};

// Types where a bedroom count is meaningless — bedrooms=0 means "not a
// dwelling" there, not "studio".
const BEDROOMLESS_TYPES = new Set(['plot', 'commercial']);

function propertyTypeLabel(deal: DealSeoInput): string {
  return PROPERTY_TYPE_LABELS[deal.propertyType ?? ''] ?? 'Property';
}

/** "Studio Apartment", "2BR Villa" — or the bare type for plot/commercial. */
function unitLabel(deal: DealSeoInput): string {
  const type = propertyTypeLabel(deal);
  if (BEDROOMLESS_TYPES.has(deal.propertyType ?? '')) return type;
  return `${deal.bedrooms === 0 ? 'Studio' : `${deal.bedrooms}BR`} ${type}`;
}

function locationLabel(deal: DealSeoInput): string {
  const building = deal.building?.trim();
  if (building && building !== deal.community) return `${building}, ${deal.community}`;
  return deal.community;
}

export function buildDealTitle(deal: DealSeoInput): string {
  return `${deal.buildingTitle} — ${unitLabel(deal)} in ${deal.community} | ${APP.name}`;
}

export function buildDealDescription(deal: DealSeoInput): string {
  const type = propertyTypeLabel(deal).toLowerCase();
  const location = locationLabel(deal);

  if (deal.status === 'SOLD') {
    return `This ${type} in ${location} has been sold. Browse current distress deals on ${APP.name}.`;
  }

  const parts: string[] = [
    `Verified ${deal.dealType === 'off_plan' ? 'off-plan' : 'ready'} ${type} in ${location}.`,
  ];
  if (deal.askingAed > 0) parts.push(`Asking ${formatAed(deal.askingAed)}.`);

  const mode = discountDisplayMode(deal.dldConfidence, deal.dldScope, deal.discountPct);
  if (mode === 'developer') {
    parts.push(`${formatDiscountMagnitude(deal.discountPct)}% below original price.`);
  } else if (mode !== 'suppressed') {
    parts.push(`${formatDiscountMagnitude(deal.discountPct)}% below market.`);
  }

  parts.push('View photos, details and advisor contact.');
  return parts.join(' ');
}

function dealImage(deal: DealSeoInput): SeoImage {
  // Cover photo URLs are absolute (Supabase storage); dimensions are not
  // persisted, so width/height stay unset and applySeoMeta drops those tags.
  if (deal.imageUrl) return { url: deal.imageUrl, alt: deal.imageLabel };
  return DEFAULT_OG_IMAGE;
}

export function buildDealSeoMeta(deal: DealSeoInput): SeoMeta {
  const canonicalPath = `/deals/${deal.slug}`;
  return {
    title: buildDealTitle(deal),
    description: buildDealDescription(deal),
    canonicalPath,
    image: dealImage(deal),
    jsonLd: [
      {
        id: 'breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Deals', item: `${SEO_ORIGIN}/` },
            {
              '@type': 'ListItem',
              position: 2,
              name: deal.buildingTitle,
              item: `${SEO_ORIGIN}${canonicalPath}`,
            },
          ],
        },
      },
    ],
  };
}
